import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { WebhookKeyWithPlain } from "../../types";

interface Props {
  keyData: WebhookKeyWithPlain | null;
  context: "created" | "rotated";
  onClose: () => void;
}

const CONTEXT_COPY = {
  created: {
    title: "Chiave creata",
    description: "La chiave è stata creata correttamente.",
  },
  rotated: {
    title: "Chiave ruotata",
    description:
      "Una nuova chiave è stata generata. La chiave precedente è stata gestita secondo l'opzione di rotazione selezionata.",
  },
} as const;

export default function WebhookKeyRevealModal({
  keyData,
  context,
  onClose,
}: Props) {
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  useEffect(() => {
    if (!keyData) {
      setShowKey(false);
      setCopied(false);
      setCopyError(false);
    }
  }, [keyData]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  if (!keyData) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(keyData.keyPlain);
      setCopied(true);
      setCopyError(false);
    } catch {
      setCopyError(true);
    }
  };

  const copyTooltip = copyError
    ? "Copia non riuscita — selezionare manualmente"
    : copied
    ? "Copiato!"
    : "Copia negli appunti";

  return (
    <Dialog
      open
      onClose={() => {
        /* no-op: chiusura solo via bottone esplicito */
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{CONTEXT_COPY[context].title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
          {CONTEXT_COPY[context].description}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Partner
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {keyData.partnerName}{" "}
            <Typography component="span" variant="caption" color="text.secondary" fontFamily="monospace">
              ({keyData.partnerId})
            </Typography>
          </Typography>
        </Box>

        <Alert severity="warning" sx={{ mb: 2 }}>
          Questa è l'unica volta che potrai vedere la chiave completa.
          Salvala ora in un luogo sicuro — il backend non conserva il
          valore in chiaro.
        </Alert>

        <TextField
          label="Chiave (plaintext)"
          value={keyData.keyPlain}
          type={showKey ? "text" : "password"}
          fullWidth
          InputProps={{
            readOnly: true,
            sx: { fontFamily: "monospace", fontSize: "0.85rem" },
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={showKey ? "Nascondi" : "Mostra"}>
                  <IconButton size="small" onClick={() => setShowKey((s) => !s)}>
                    {showKey ? (
                      <VisibilityOffIcon fontSize="small" />
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title={copyTooltip}>
                  <IconButton size="small" onClick={copy}>
                    <ContentCopyIcon fontSize="small" color={copied ? "success" : "inherit"} />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />

        {copyError && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            Copia automatica non riuscita. Mostra la chiave e selezionala manualmente.
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Prefisso pubblico (per identificazione)
          </Typography>
          <Typography variant="body2" fontFamily="monospace">
            {keyData.keyPrefix}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={onClose}>
          Ho copiato la chiave
        </Button>
      </DialogActions>
    </Dialog>
  );
}
